import React, { useState, useEffect } from 'react';
import { notesAPI } from '../services/api';
import { FileText, Plus, Edit2, Trash2, Search, Calendar, Tag, X, Save } from 'lucide-react';

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Form states
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteTags, setNoteTags] = useState('');

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await notesAPI.getAll(searchQuery || null);
      setNotes(data);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadNotes();
  };

  const handleCreateNote = async () => {
    try {
      const newNote = {
        title: noteTitle || 'Untitled Note',
        content: noteContent,
        tags: noteTags ? noteTags.split(',').map(t => t.trim()) : []
      };
      
      await notesAPI.create(newNote);
      setShowCreateModal(false);
      resetForm();
      loadNotes();
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Failed to create note');
    }
  };

  const handleUpdateNote = async () => {
    if (!selectedNote) return;
    
    try {
      await notesAPI.update(selectedNote.id, {
        title: noteTitle,
        content: noteContent,
        tags: noteTags ? noteTags.split(',').map(t => t.trim()) : []
      });
      
      setIsEditing(false);
      loadNotes();
      
      // Update selected note with new data
      setSelectedNote({
        ...selectedNote,
        title: noteTitle,
        content: noteContent,
        tags: noteTags ? noteTags.split(',').map(t => t.trim()) : []
      });
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await notesAPI.delete(noteId);
      if (selectedNote?.id === noteId) {
        setSelectedNote(null);
      }
      loadNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const openNote = (note) => {
    setSelectedNote(note);
    setNoteTitle(note.title);
    setNoteContent(note.content);
    setNoteTags(note.tags?.join(', ') || '');
    setIsEditing(false);
  };

  const resetForm = () => {
    setNoteTitle('');
    setNoteContent('');
    setNoteTags('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-transparent p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <FileText className="text-white" size={32} />
              Notes Vault
            </h1>
            <p className="text-gray-400">Your personal note collection</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-gradient-to-r bg-white hover:bg-gray-100 text-black rounded-lg transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            New Note
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full bg-white/10 border border-white/60 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-gradient-to-r bg-white hover:bg-gray-100 text-black rounded-lg transition-colors"
            >
              Search
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Notes List */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-4">
              <h2 className="text-xl font-semibold text-white mb-4">
                All Notes ({notes.length})
              </h2>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <FileText className="mx-auto mb-2" size={48} />
                  <p>No notes yet</p>
                  <p className="text-sm">Create your first note!</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      onClick={() => openNote(note)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedNote?.id === note.id
                          ? 'bg-gradient-to-r bg-white/50 border-2 border-blue-400'
                          : 'bg-white/5 hover:bg-white/10 border-2 border-transparent'
                      }`}
                    >
                      <h3 className="font-semibold text-white mb-1 truncate">
                        {note.title}
                      </h3>
                      <p className="text-xs text-gray-400 mb-2">
                        {formatDate(note.updated_at)}
                      </p>
                      <p className="text-sm text-gray-300 line-clamp-2">
                        {note.content}
                      </p>
                      {note.tags && note.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {note.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-1 bg-white/30 text-blue-200 rounded-full"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Note Detail/Editor */}
          <div className="lg:col-span-2">
            {selectedNote ? (
              <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    {isEditing ? (
                      <input
                        type="text"
                        value={noteTitle}
                        onChange={(e) => setNoteTitle(e.target.value)}
                        className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-2 text-2xl font-bold text-white focus:outline-none focus:border-blue-500"
                        placeholder="Note title"
                      />
                    ) : (
                      <h2 className="text-2xl font-bold text-white">{selectedNote.title}</h2>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Created: {formatDate(selectedNote.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Updated: {formatDate(selectedNote.updated_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <button
                          onClick={handleUpdateNote}
                          className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                        >
                          <Save size={20} />
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setNoteTitle(selectedNote.title);
                            setNoteContent(selectedNote.content);
                            setNoteTags(selectedNote.tags?.join(', ') || '');
                          }}
                          className="p-2 bg-transparent hover:bg-white/5 text-white rounded-lg transition-colors"
                        >
                          <X size={20} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setIsEditing(true)}
                          className="p-2 bg-gradient-to-r bg-white hover:bg-gray-100 text-black rounded-lg transition-colors"
                        >
                          <Edit2 size={20} />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(selectedNote.id)}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                        >
                          <Trash2 size={20} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {isEditing ? (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={noteTags}
                      onChange={(e) => setNoteTags(e.target.value)}
                      className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                      placeholder="work, personal, ideas"
                    />
                  </div>
                ) : selectedNote.tags && selectedNote.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedNote.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-white/30 text-blue-200 rounded-full text-sm flex items-center gap-1"
                      >
                        <Tag size={14} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Content */}
                <div className="mt-4">
                  {isEditing ? (
                    <textarea
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 min-h-[400px] resize-none"
                      placeholder="Write your note here..."
                    />
                  ) : (
                    <div className="bg-white/5 rounded-lg p-4 min-h-[400px]">
                      <pre className="text-gray-300 whitespace-pre-wrap font-sans">
                        {selectedNote.content}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6 flex items-center justify-center min-h-[500px]">
                <div className="text-center text-gray-400">
                  <FileText className="mx-auto mb-4" size={64} />
                  <p className="text-xl">Select a note to view or edit</p>
                  <p className="text-sm mt-2">Or create a new note to get started</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Note Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-white/60 rounded-2xl shadow-2xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white">Create New Note</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                  placeholder="Note title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={noteTags}
                  onChange={(e) => setNoteTags(e.target.value)}
                  className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                  placeholder="work, personal, ideas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Content
                </label>
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  className="w-full bg-white/10 border border-white/60 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 min-h-[300px] resize-none"
                  placeholder="Write your note here..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-6 py-2 bg-transparent hover:bg-white/5 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNote}
                className="px-6 py-2 bg-gradient-to-r bg-white hover:bg-gray-100 text-black rounded-lg transition-colors flex items-center gap-2"
              >
                <Save size={20} />
                Create Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;
